/**
 * MobileSettings - Mobile control preferences stored in localStorage
 *
 * Manages user preferences for mobile controls including control mode,
 * handedness, sizes, opacity, and haptic feedback.
 */

/**
 * Control mode for direction input
 */
export enum ControlMode {
  /** Virtual joystick for direction control (default) */
  JOYSTICK = 'joystick',
  /** Follow finger position (relative to player) */
  POINTER = 'pointer',
}

/**
 * Handedness for control layout
 */
export enum Handedness {
  /** Joystick on left, buttons on right */
  RIGHT = 'right',
  /** Joystick on right, buttons on left */
  LEFT = 'left',
}

/**
 * Size options for controls
 */
export enum ControlSize {
  SMALL = 'small',
  MEDIUM = 'medium',
  LARGE = 'large',
}

/**
 * Mobile settings configuration
 */
export interface MobileSettingsConfig {
  /** Control mode (joystick or pointer) */
  controlMode: ControlMode;
  /** Handedness (left or right handed) */
  handedness: Handedness;
  /** Joystick size */
  joystickSize: ControlSize;
  /** Button size */
  buttonSize: ControlSize;
  /** Joystick opacity (0.3 - 1.0) */
  joystickOpacity: number;
  /** Whether vibration/haptic feedback is enabled */
  vibrationEnabled: boolean;
  /** Whether to show mobile controls on touch devices */
  mobileControlsEnabled: boolean;
}

/**
 * Default settings
 */
const DEFAULT_SETTINGS: MobileSettingsConfig = {
  controlMode: ControlMode.JOYSTICK,
  handedness: Handedness.RIGHT,
  joystickSize: ControlSize.MEDIUM,
  buttonSize: ControlSize.MEDIUM,
  joystickOpacity: 0.7,
  vibrationEnabled: true,
  mobileControlsEnabled: true,
};

/**
 * LocalStorage key
 */
const STORAGE_KEY = 'wooorms_mobile_settings';

/**
 * Size multipliers for controls
 */
export const SIZE_MULTIPLIERS: Record<ControlSize, number> = {
  [ControlSize.SMALL]: 0.75,
  [ControlSize.MEDIUM]: 1.0,
  [ControlSize.LARGE]: 1.25,
};

/**
 * Base sizes in pixels (at medium size)
 */
export const BASE_SIZES = {
  /** Joystick outer ring diameter */
  joystickOuter: 120,
  /** Joystick inner handle diameter */
  joystickInner: 50,
  /** Action button diameter */
  button: 70,
  /** Button spacing */
  buttonSpacing: 15,
  /** Padding from screen edge */
  edgePadding: 20,
};

/**
 * Get joystick size in pixels based on setting
 */
export function getJoystickSize(size: ControlSize): { outer: number; inner: number } {
  const multiplier = SIZE_MULTIPLIERS[size];
  return {
    outer: BASE_SIZES.joystickOuter * multiplier,
    inner: BASE_SIZES.joystickInner * multiplier,
  };
}

/**
 * Get button size in pixels based on setting
 */
export function getButtonSize(size: ControlSize): number {
  return BASE_SIZES.button * SIZE_MULTIPLIERS[size];
}

/**
 * MobileSettings - Singleton for managing mobile settings
 */
export class MobileSettings {
  private static _instance: MobileSettings | null = null;

  private _settings: MobileSettingsConfig;
  private _listeners: Set<(settings: MobileSettingsConfig) => void> = new Set();

  private constructor() {
    this._settings = this._load();
  }

  static getInstance(): MobileSettings {
    if (!MobileSettings._instance) {
      MobileSettings._instance = new MobileSettings();
    }
    return MobileSettings._instance;
  }

  /**
   * Load settings from localStorage
   */
  private _load(): MobileSettingsConfig {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Merge with defaults to handle new settings being added
        return { ...DEFAULT_SETTINGS, ...parsed };
      }
    } catch (error) {
      console.warn('Failed to load mobile settings:', error);
    }
    return { ...DEFAULT_SETTINGS };
  }

  /**
   * Save settings to localStorage
   */
  private _save(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this._settings));
    } catch (error) {
      console.warn('Failed to save mobile settings:', error);
    }
  }

  /**
   * Notify listeners of settings change
   */
  private _notify(): void {
    this._listeners.forEach(listener => listener(this._settings));
  }

  /**
   * Get all settings
   */
  get settings(): MobileSettingsConfig {
    return { ...this._settings };
  }

  /**
   * Get control mode
   */
  get controlMode(): ControlMode {
    return this._settings.controlMode;
  }

  /**
   * Set control mode
   */
  set controlMode(value: ControlMode) {
    this._settings.controlMode = value;
    this._save();
    this._notify();
  }

  /**
   * Get handedness
   */
  get handedness(): Handedness {
    return this._settings.handedness;
  }

  /**
   * Set handedness
   */
  set handedness(value: Handedness) {
    this._settings.handedness = value;
    this._save();
    this._notify();
  }

  /**
   * Get joystick size
   */
  get joystickSize(): ControlSize {
    return this._settings.joystickSize;
  }

  /**
   * Set joystick size
   */
  set joystickSize(value: ControlSize) {
    this._settings.joystickSize = value;
    this._save();
    this._notify();
  }

  /**
   * Get button size
   */
  get buttonSize(): ControlSize {
    return this._settings.buttonSize;
  }

  /**
   * Set button size
   */
  set buttonSize(value: ControlSize) {
    this._settings.buttonSize = value;
    this._save();
    this._notify();
  }

  /**
   * Get joystick opacity
   */
  get joystickOpacity(): number {
    return this._settings.joystickOpacity;
  }

  /**
   * Set joystick opacity (clamped to 0.3 - 1.0)
   */
  set joystickOpacity(value: number) {
    this._settings.joystickOpacity = Math.max(0.3, Math.min(1.0, value));
    this._save();
    this._notify();
  }

  /**
   * Get vibration enabled
   */
  get vibrationEnabled(): boolean {
    return this._settings.vibrationEnabled;
  }

  /**
   * Set vibration enabled
   */
  set vibrationEnabled(value: boolean) {
    this._settings.vibrationEnabled = value;
    this._save();
    this._notify();
  }

  /**
   * Get mobile controls enabled
   */
  get mobileControlsEnabled(): boolean {
    return this._settings.mobileControlsEnabled;
  }

  /**
   * Set mobile controls enabled
   */
  set mobileControlsEnabled(value: boolean) {
    this._settings.mobileControlsEnabled = value;
    this._save();
    this._notify();
  }

  /**
   * Check if joystick mode is active
   */
  get isJoystickMode(): boolean {
    return this._settings.controlMode === ControlMode.JOYSTICK;
  }

  /**
   * Check if pointer mode is active
   */
  get isPointerMode(): boolean {
    return this._settings.controlMode === ControlMode.POINTER;
  }

  /**
   * Check if left-handed mode
   */
  get isLeftHanded(): boolean {
    return this._settings.handedness === Handedness.LEFT;
  }

  /**
   * Check if right-handed mode
   */
  get isRightHanded(): boolean {
    return this._settings.handedness === Handedness.RIGHT;
  }

  /**
   * Get calculated joystick sizes in pixels
   */
  getJoystickPixelSizes(): { outer: number; inner: number } {
    return getJoystickSize(this._settings.joystickSize);
  }

  /**
   * Get calculated button size in pixels
   */
  getButtonPixelSize(): number {
    return getButtonSize(this._settings.buttonSize);
  }

  /**
   * Update multiple settings at once
   */
  updateSettings(updates: Partial<MobileSettingsConfig>): void {
    Object.assign(this._settings, updates);

    // Clamp opacity
    if (updates.joystickOpacity !== undefined) {
      this._settings.joystickOpacity = Math.max(0.3, Math.min(1.0, this._settings.joystickOpacity));
    }

    this._save();
    this._notify();
  }

  /**
   * Reset to default settings
   */
  reset(): void {
    this._settings = { ...DEFAULT_SETTINGS };
    this._save();
    this._notify();
  }

  /**
   * Add settings change listener
   */
  addListener(callback: (settings: MobileSettingsConfig) => void): void {
    this._listeners.add(callback);
  }

  /**
   * Remove settings change listener
   */
  removeListener(callback: (settings: MobileSettingsConfig) => void): void {
    this._listeners.delete(callback);
  }

  /**
   * Trigger haptic feedback if enabled
   */
  vibrate(pattern: number | number[] = 10): void {
    if (this._settings.vibrationEnabled && 'vibrate' in navigator) {
      try {
        navigator.vibrate(pattern);
      } catch (error) {
        // Vibration not supported or failed
      }
    }
  }

  /**
   * Trigger short haptic tap
   */
  hapticTap(): void {
    this.vibrate(10);
  }

  /**
   * Trigger medium haptic feedback
   */
  hapticMedium(): void {
    this.vibrate(25);
  }

  /**
   * Trigger strong haptic feedback
   */
  hapticStrong(): void {
    this.vibrate(50);
  }

  /**
   * Clean up
   */
  destroy(): void {
    this._listeners.clear();
    MobileSettings._instance = null;
  }
}
